import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, FileText } from "lucide-react";

interface NoteData {
  question_id: number;
  question_key?: string;
  comment: string;
  score: number | null;
  evidence_links: string[] | null;
}

interface ReviewerNotesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domainName: string;
  notes: NoteData[];
}

export function ReviewerNotesDrawer({ 
  open, 
  onOpenChange, 
  domainName, 
  notes 
}: ReviewerNotesDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            Reviewer Notes — {domainName}
          </SheetTitle>
        </SheetHeader>
        
        <div className="py-4 space-y-3">
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No reviewer notes for this domain.
            </p>
          ) : (
            notes.map((note, index) => (
              <Card key={`${note.question_id}-${index}`} className="border">
                <CardContent className="p-4 space-y-3">
                  {/* Question label */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-primary">
                      {note.question_key || `Question ${note.question_id}`}
                    </span>
                    {note.score !== null && note.score !== undefined && (
                      <Badge variant="secondary" className="text-xs">
                        Score: {note.score}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Note text */}
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {note.comment}
                  </p>
                  
                  {/* Evidence links count */}
                  {note.evidence_links && note.evidence_links.length > 0 && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {note.evidence_links.length} evidence file{note.evidence_links.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
